import { useCallback, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Tooltip from '@mui/material/Tooltip';
import type { GeneratedMessage } from '@trinity/chat/types/message-generation';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { Constants } from '@trinity/chat-components/components/Theme';

type Props = {
  segmentId?: string;
  segmentationSessionId?: string;
  segmentName?: string;
  onMessageClick?: (message: GeneratedMessage) => void;
};

export const MessageLibrarySidePanel = ({ segmentId, segmentationSessionId, segmentName, onMessageClick }: Props) => {
  const apiClient = useChatApiClient();

  // Fetch messages for the segment
  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['generated-messages', { segmentId, segmentationSessionId }],
    queryFn: async () => {
      if (!segmentId) return [];
      const params: any = { segmentId };
      if (segmentationSessionId) {
        params.segmentationSessionId = segmentationSessionId;
      }
      return apiClient.messageGeneration.getAllMessages(params);
    },
    enabled: !!segmentId,
  });

  const handleMessageItemClick = useCallback(
    (message: GeneratedMessage) => {
      onMessageClick?.(message);
    },
    [onMessageClick]
  );

  // Generate message identifier (E1, C1, etc.) - matches MessageGenerationTable logic
  const getMessageId = (message: GeneratedMessage, index: number) => {
    // Try objectiveCategory first, then message category, then fallback to 'M'
    const source = message.objectiveCategory || message.category;
    if (!source) return `M${index + 1}`;
    const firstLetter = source.trim().charAt(0).toUpperCase();

    // Count how many messages of the same category come before this one
    const categoryCount = messages
      .slice(0, index + 1)
      .filter((m) => {
        const mSource = message.objectiveCategory || m.category;
        return mSource && mSource.trim().charAt(0).toUpperCase() === firstLetter;
      }).length;

    return `${firstLetter}${categoryCount}`;
  };

  return (
    <Root>
      <Content>
          <Header>
            <StyledAutoAwesomeIcon />
            <Title>Segment Messages</Title>
            <MessageCount>{messages.length}</MessageCount>
          </Header>
          <MessagesScrollArea>
            {isLoading && (
              <EmptyState>Loading messages...</EmptyState>
            )}
            {!isLoading && messages.length === 0 && (
              <EmptyState>No messages generated for this segment</EmptyState>
            )}
            {!isLoading && messages.map((message, index) => {
              const tooltipContent = (
                <TooltipContent>
                  <TooltipTitle>{message.title}</TooltipTitle>
                  <TooltipBody>{message.body}</TooltipBody>
                  <TooltipFooter>
                    Click to ask this message to {segmentName || 'segment'}
                  </TooltipFooter>
                </TooltipContent>
              );

              return (
                <Tooltip
                  key={message.id}
                  title={tooltipContent}
                  placement="left"
                  arrow
                  enterDelay={300}
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      },
                    ],
                  }}
                >
                  <MessageItem
                    onClick={() => handleMessageItemClick(message)}
                  >
                    <MessageHeader>
                      <MessageId>{getMessageId(message, index)}</MessageId>
                      {message.category && (
                        <CategoryBadge>{message.category}</CategoryBadge>
                      )}
                    </MessageHeader>
                    <MessageTitle>{message.title}</MessageTitle>
                    <MessageBody>{message.body}</MessageBody>
                    {message.confidence && (
                      <ConfidenceRow>
                        <ConfidenceLabel>Confidence:</ConfidenceLabel>
                        <ConfidenceValue>{message.confidence}</ConfidenceValue>
                      </ConfidenceRow>
                    )}
                  </MessageItem>
                </Tooltip>
              );
            })}
          </MessagesScrollArea>
        </Content>
    </Root>
  );
};

MessageLibrarySidePanel.displayName = 'MessageLibrary.SidePanel';

const Root = styled.div`
  box-sizing: border-box;
  border-radius: ${Constants.Modal.BorderRadius}px;
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.palette.gray.light2};
  position: relative;
`;

const Content = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background: var(--pri-01);
`;

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px 0;
  margin-bottom: 8px;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  flex: 1;
`;

const MessageCount = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #666;
  background: #e5e5e5;
  padding: 4px 8px;
  border-radius: 12px;
  line-height: 1;
`;

const MessagesScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const MessageItem = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.palette.sentiment.primary.main};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const MessageId = styled.div`
  font-size: 11px;
  font-family: var(--font-bold);
  color: ${({ theme }) => theme.palette.sentiment.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CategoryBadge = styled.div`
  font-size: 10px;
  color: #666;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: capitalize;
`;

const MessageTitle = styled.div`
  font-size: 13px;
  font-family: var(--font-bold);
  color: #333;
  margin-bottom: 4px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

const MessageBody = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ConfidenceRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f0f0f0;
`;

const ConfidenceLabel = styled.span`
  font-size: 10px;
  color: #999;
  margin-right: 4px;
`;

const ConfidenceValue = styled.span`
  font-size: 10px;
  font-family: var(--font-bold);
  color: #666;
  text-transform: capitalize;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 350px;
`;

const TooltipTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.4;
`;

const TooltipBody = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  white-space: pre-wrap;
`;

const TooltipFooter = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 4px;
`;
